// ============================================================================
// API — Productos Relacionados (panel-cliente).
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';
import { tieneFeature } from '@plataforma/utils';

import { obtenerEstadoSesion } from '../estado-sesion.js';

function empresaIdActual() {
  const sesion = obtenerEstadoSesion();
  return sesion.claims?.empresa_id ?? sesion.empresa?.id ?? null;
}

function empresaActual() {
  const sesion = obtenerEstadoSesion();
  return sesion.empresa ?? null;
}

function requiereProductosRelacionados(operacion) {
  const empresa = empresaActual();
  if (!tieneFeature(empresa, 'productos_relacionados')) {
    return Promise.resolve({
      data: null,
      error: {
        message:
          'La funcionalidad Productos Relacionados no está disponible en tu plan. Mejora a Medio o Pro.',
        plan_requerido: 'Medio',
      },
    });
  }
  return operacion();
}

/**
 * Consulta los productos relacionados vinculados a un producto.
 * @param {string} productoId
 */
export async function listarProductosRelacionados(productoId) {
  return supabaseClient()
    .from('producto_relacionado')
    .select(
      'producto_id, producto_relacionado_id, ' +
        'relacionado:producto_relacionado_id(' +
        '  id, nombre, slug, precio, estado, ' +
        '  imagenes:producto_imagen(id, url, es_principal, orden)' +
        ')',
    )
    .eq('producto_id', productoId);
}

/**
 * Agrega una relación entre productoId y productoRelacionadoId.
 * Valida plan feature, pertenencia de tenant, autorreferencia y duplicados.
 *
 * @param {string} productoId
 * @param {string} productoRelacionadoId
 */
export async function agregarProductoRelacionado(productoId, productoRelacionadoId) {
  return requiereProductosRelacionados(async () => {
    if (!productoId || !productoRelacionadoId) {
      return { data: null, error: { message: 'Los identificadores de productos son requeridos.' } };
    }

    if (productoId === productoRelacionadoId) {
      return {
        data: null,
        error: { message: 'Un producto no se puede relacionar consigo mismo.' },
      };
    }

    const empresaId = empresaIdActual();
    if (!empresaId) {
      return { data: null, error: { message: 'No hay una sesión de empresa activa.' } };
    }

    // Validar que ambos productos pertenecen a la empresa actual
    const { data: productosEmpresa, error: errorValidacion } = await supabaseClient()
      .from('producto')
      .select('id, empresa_id')
      .in('id', [productoId, productoRelacionadoId])
      .eq('empresa_id', empresaId)
      .is('eliminado_en', null);

    if (errorValidacion) {
      return { data: null, error: errorValidacion };
    }

    const idsValidos = new Set((productosEmpresa ?? []).map((p) => p.id));
    if (!idsValidos.has(productoId) || !idsValidos.has(productoRelacionadoId)) {
      return { data: null, error: { message: 'Los productos deben pertenecer a tu empresa.' } };
    }

    return supabaseClient()
      .from('producto_relacionado')
      .insert({
        producto_id: productoId,
        producto_relacionado_id: productoRelacionadoId,
      })
      .select()
      .single();
  });
}

/**
 * Elimina la relación de productos relacionados.
 * @param {string} productoId
 * @param {string} productoRelacionadoId
 */
export async function eliminarProductoRelacionado(productoId, productoRelacionadoId) {
  return requiereProductosRelacionados(() => {
    return supabaseClient()
      .from('producto_relacionado')
      .delete()
      .eq('producto_id', productoId)
      .eq('producto_relacionado_id', productoRelacionadoId);
  });
}
